import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useState, type FormEvent } from "react";
import { useOutletContext } from "react-router-dom";
import { adminApi, withQuery } from "../../api/admin";
import { API_URL } from "../../api/client";
import type { AdminAuditRow } from "../../api/types";
import { formatDateTime, utcTooltip } from "../../shared/format";
import { Button, Card, DataTable, Input, Spinner } from "../../ui";
import { Pager, useCursorPages } from "./shared";

const columns: ColumnDef<AdminAuditRow, unknown>[] = [
  {
    id: "created_at",
    header: "When",
    cell: ({ row }) => (
      <span title={utcTooltip(row.original.created_at)}>
        {formatDateTime(row.original.created_at)}
      </span>
    ),
  },
  {
    id: "actor",
    header: "Actor",
    cell: ({ row }) => `${row.original.actor_type}:${row.original.actor_id}`,
  },
  { accessorKey: "action", header: "Action" },
  {
    id: "target",
    header: "Target",
    cell: ({ row }) =>
      `${row.original.target_type}${row.original.target_id ? `:${row.original.target_id}` : ""}`,
  },
  {
    id: "diff",
    header: "Diff",
    cell: ({ row }) =>
      Object.keys(row.original.diff ?? {}).length ? (
        <code className="text-xs">{JSON.stringify(row.original.diff)}</code>
      ) : (
        "—"
      ),
  },
];

export function AuditPage() {
  const { product } = useOutletContext<{ product: string | null }>();
  const EMPTY = { actor: "", target: "", action: "" };
  const [draft, setDraft] = useState(EMPTY);
  const [filters, setFilters] = useState(EMPTY);
  const pages = useCursorPages();
  const query = useQuery({
    queryKey: ["admin", "audit", filters, pages.cursor],
    queryFn: () =>
      adminApi.audit({
        ...filters,
        product: product ?? undefined,
        cursor: pages.cursor,
      }),
  });
  const csvUrl = `${API_URL}${withQuery("/admin/audit", { ...filters, format: "csv" })}`;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setFilters(draft);
    pages.reset();
  };
  return (
    <Card>
      <h1 className="mb-4 text-xl font-semibold">Audit log</h1>
      <form
        onSubmit={submit}
        className="mb-4 grid gap-3 md:grid-cols-4"
        aria-label="Filter audit"
      >
        <Input
          label="Actor id"
          value={draft.actor}
          onChange={(e) => setDraft({ ...draft, actor: e.target.value })}
        />
        <Input
          label="Target id"
          value={draft.target}
          onChange={(e) => setDraft({ ...draft, target: e.target.value })}
        />
        <Input
          label="Action"
          placeholder="admin.suspend"
          value={draft.action}
          onChange={(e) => setDraft({ ...draft, action: e.target.value })}
        />
        <div className="flex items-end gap-2">
          <Button type="submit">Filter</Button>
          <a
            className="rounded-md border border-slate-300 px-4 py-2 text-sm"
            href={csvUrl}
            download
          >
            Download CSV
          </a>
        </div>
      </form>
      {query.isLoading ? <Spinner /> : null}
      {query.data ? (
        <DataTable<AdminAuditRow>
          columns={columns}
          data={query.data.items}
          emptyMessage="No audit entries match."
        />
      ) : null}
      <Pager pages={pages} nextCursor={query.data?.next_cursor} />
    </Card>
  );
}
